import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * BrownianElement - Animated Particle Component
 * 
 * Simulates Brownian motion (random molecular movement) for educational physics visualizations.
 * The particle moves randomly within defined boundaries using requestAnimationFrame for smooth animation.
 * 
 * This component is used to visualize molecular movement, heat transfer, and thermodynamic concepts
 * in educational simulations where particle behavior needs to be demonstrated.
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {number} props.X - Initial X coordinate position
 * @param {number} props.Y - Initial Y coordinate position
 * @param {number} props.minX - Minimum X boundary (left edge)
 * @param {number} props.maxX - Maximum X boundary (right edge)
 * @param {number} props.minY - Minimum Y boundary (top edge)
 * @param {number} props.maxY - Maximum Y boundary (bottom edge)
 * @param {number} [props.R=5] - Radius of the particle circle in pixels
 * @param {number} [props.Speed=1] - Speed multiplier for particle movement (higher = faster)
 * @param {string} [props.Color='red'] - Fill color of the particle (CSS color value)
 * 
 * @returns {JSX.Element} SVG circle element with animated Brownian motion
 * 
 * @example
 * // Basic particle in a bounded area
 * <BrownianElement 
 *   X={100} 
 *   Y={100} 
 *   minX={0} 
 *   maxX={200} 
 *   minY={0} 
 *   maxY={200} 
 *   R={8} 
 *   Speed={2} 
 *   Color="blue" 
 * />
 */
export function BrownianElement({ X, Y, minX, maxX, minY, maxY, R = 5, Speed = 1, Color = 'red' }) {
  // Track current particle position
  const [position, setPosition] = useState({ x: X, y: Y });
  
  // Current speed value (can be updated dynamically)
  const [speed, setSpeed] = useState(Speed);
  
  // Reference to animation frame ID for cleanup
  const animationRef = useRef(null);

  /**
   * Updates particle position with random Brownian motion
   * Movement is constrained within min/max boundaries using Math.max and Math.min
   * Random displacement: 3 * (Math.random() - 0.5) gives range of [-1.5, 1.5]
   * Multiplied by speed for velocity control
   */
  const updatePosition = useCallback(() => {
    setPosition((prev) => ({
      // Constrain X position within horizontal boundaries
      x: Math.max(minX, Math.min(prev.x + 3 * (Math.random() - 0.5) * speed, maxX)),
      // Constrain Y position within vertical boundaries
      y: Math.max(minY, Math.min(prev.y + 3 * (Math.random() - 0.5) * speed, maxY)),
    }));
    
    // Schedule next animation frame for continuous movement
    animationRef.current = requestAnimationFrame(updatePosition);
  }, [minX, maxX, minY, maxY, speed]);

  // Update speed when Speed prop changes (allows dynamic speed control)
  useEffect(() => setSpeed(Speed), [Speed]);

  // Start animation loop on mount and cleanup on unmount
  useEffect(() => {
    animationRef.current = requestAnimationFrame(updatePosition);
    
    // Cleanup: cancel animation frame to prevent memory leaks
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [updatePosition]);

  // Render SVG circle at current position
  return <circle cx={position.x} cy={position.y} r={R} fill={Color} stroke="transparent" />;
}

export default BrownianElement;
